import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || 'https://placeholder.supabase.co';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || 'placeholder';

// NOTE: the Database type scaffolding in src/types/supabase.ts is NOT
// bound to this client via `createClient<Database>`. That binding was
// attempted during the T2.1 QA sweep but surfaced 190 type errors
// because some tables in my hand-written schema drifted from the live
// production schema (e.g. `interventions.patient_id` + `date` columns
// that weren't in my Database type). Without live supabase CLI access
// to verify every column, the CLI-generated types are the correct
// source of truth. Until then:
//
//   1. src/types/supabase.ts is available as opt-in scaffolding —
//      consumers that want strict typing can import `Row<'labs'>` etc.
//      and use `as` assertions on specific queries.
//   2. When the CLI is authenticated against the project, run:
//        supabase gen types typescript --project-id <id> > src/types/supabase.ts
//      then re-add `createClient<Database>` here and fix the resulting
//      (real) type errors in a dedicated cleanup pass.
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
});

/**
 * Safe wrapper for getting the current authenticated user.
 * Uses getSession() (localStorage read, no network) instead of getUser()
 * (network request that can fail/throw in Supabase JS v2 under token refresh
 * race conditions or brief network hiccups). getSession() is always reliable
 * when the user is signed in and persistSession=true.
 *
 * Falls back to getUser() only if the session is missing but a JWT is present
 * in storage — handles edge-case stale-session scenarios gracefully.
 */
export async function getCurrentUser() {
  const sessionRes = await supabase.auth.getSession().catch(() => null);
  if (sessionRes?.data?.session?.user) {
    return sessionRes.data.session.user;
  }
  // Fallback: try network call in case session is missing but token exists
  const userRes = await supabase.auth.getUser().catch(() => null);
  return userRes?.data?.user ?? null;
}
